import { Injectable, Inject, InjectionToken } from '@angular/core';
import { Observable, Subject, throwError, ReplaySubject } from 'rxjs';
import { map, pluck, tap, take, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

export type SignsByTag = Map<Tag, Array<Sign>>;

export type Tag = string;

export interface Sign {
  id: string;
  name: string;
  video: string;
  thumb: string;
  thumbGif: string;
}

export interface SignDb {
  signs: Array<SignEntry>;
}

export interface SignEntry {
  id: string;
  name: string;
  video: string;
  thumb: string;
  thumbGif: string;
  tags: Array<string>;
}

export const SIGNS_URL = new InjectionToken<string>('URL for signs database');

export const groupByTag = (results: Array<SignEntry>): SignsByTag => results.reduce(
  (acc: SignsByTag, result: SignEntry): SignsByTag => {
    const sign: Sign = result;
    result.tags.forEach(tag => {
      const tagSigns = acc.get(tag) || [];
      acc.set(tag, [...tagSigns, sign]);
    });
    return acc;
  },
  new Map<Tag, Array<Sign>>(),
);

@Injectable({
  providedIn: 'root'
})
export class SignService {
  private loads = new ReplaySubject();

  private db: Observable<SignDb> = this.loads.pipe(
    take(1),
    switchMap(() => this.http.get<SignDb>(this.signsUrl))
  );

  signsByTag: Observable<SignsByTag> = this.db.pipe(
    pluck('signs'),
    map(groupByTag),
  );

  constructor(
    @Inject(SIGNS_URL) private readonly signsUrl: string,
    private http: HttpClient,
  ) {}

  load(): void {
    this.loads.next();
  }

  getSignById(id: string): Observable<Sign> {
    this.load();
    return this.db.pipe(
      map((results): Sign => {
        const found = results.signs.find(sign => sign.id === id);
        if (found === undefined) {
          throw new Error('Sign not found');
        }
        return found;
      })
    );
  }

  filterSigns(search: string): Observable<Array<Sign>> {
    const filterValue = search.toLowerCase();

    return this.db.pipe(
      pluck('signs'),
      map((allSigns: Array<SignEntry>) => allSigns.filter(
        (sign => sign.name.toLowerCase().indexOf(filterValue) !== -1),
      )),
    );
  }
}
